/**
 * DSA Report API
 * Handles DSA report-related API endpoints
 */

import { executeRequest } from './hooks/useApiQuery';

// Problem types
export type ProblemDifficulty = 'easy' | 'medium' | 'hard';

export interface Problem {
  title: string;
  description: string;
  difficulty: ProblemDifficulty;
  language: string;
}

// Submission types
export interface Submission {
  code: string;
  language: string;
  submittedAt: string;
  passedCount: number;
  totalCount: number;
}

// Evaluation metric types
export interface CodeQualityMetric {
  rating: number;
  remarks: string;
}

export interface ComplexityMetric {
  stated: string;
  actual: string;
  rating: number;
}

export interface EvaluationData {
  overall_rating: number;
  summary: string;
  code_quality: {
    correctness: CodeQualityMetric;
    efficiency: CodeQualityMetric;
    readability: CodeQualityMetric;
  };
  problem_solving: {
    approach: CodeQualityMetric;
    algorithm_choice: CodeQualityMetric;
    optimization: CodeQualityMetric;
  };
  complexity_analysis: {
    time_complexity: ComplexityMetric;
    space_complexity: ComplexityMetric;
  };
  communication: {
    explanation: CodeQualityMetric;
    thought_process: CodeQualityMetric;
  };
  strengths: string[];
  improvements: string[];
}

export interface Evaluation {
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  data?: EvaluationData;
  error?: string;
}

// Interview details
export interface InterviewDetails {
  id?: string;
  user_name?: string;
  user_email?: string;
  interview_status?: string;
  duration?: number;
  thread_id?: string;
  rating?: number;
  type?: string;
  version?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Interview message
export interface InterviewMessage {
  id: string;
  message: string;
  role: string;
  index: number;
  timestamp: string;
  isInstruction?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Main DSA Report response type
export interface DSAReportStatusResponse {
  interviewId: string;
  generationStatus: 'pending' | 'generating' | 'completed' | 'failed';
  problem: Problem;
  submission: Submission;
  evaluation: Evaluation;
  interviewDetails?: InterviewDetails | null;
  interviewMessages?: InterviewMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface VideoStatusResponse {
  interviewId: string;
  isMerged: boolean;
  mergedUrl: string | null;
  statusOfRecording: 'in_progress' | 'completed' | null;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Get DSA report generation status
 */
export async function getReportStatus(interviewId: string): Promise<DSAReportStatusResponse> {
  const response = await executeRequest<ApiResponse<DSAReportStatusResponse>>({
    url: `/report/status/${interviewId}`,
    method: 'GET',
  });
  return response.data;
}

/**
 * Trigger DSA report generation
 */
export async function generateReport(interviewId: string): Promise<{ status: string; message: string }> {
  const response = await executeRequest<ApiResponse<{ status: string; message: string }>>({
    url: `/report/generate/${interviewId}`,
    method: 'POST',
  });
  return response.data;
}

/**
 * Get video processing status
 */
export async function getVideoStatus(interviewId: string): Promise<VideoStatusResponse> {
  const response = await executeRequest<ApiResponse<VideoStatusResponse>>({
    url: `/report/video-status/${interviewId}`,
    method: 'GET',
  });
  return response.data;
}

// Legacy types for backward compatibility (deprecated)
/** @deprecated Use DSAReportStatusResponse instead */
export type ReportStatusResponse = DSAReportStatusResponse;
